import React, { useEffect, useMemo } from 'react';
import InfiniteMediaLibrary, {
  GridTileType,
} from '~/App/Modules/MyLibrary/Components/InfiniteMediaLibrary';
import { usePublishSelectModalContext } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishSelectModalContext';
import EmptyRecordingUploadsAndResponses from '~/App/Modules/MyLibrary/Components/EmptyRecordingsUploadsAndResponses';
import { InfiniteMediaItemConfigProps } from '~/App/Modules/MyLibrary/Context/infinite-media-item-config-context';
import TestimonialsAllEmpty from '../../../../Components/Empty/TestimonialsAllEmpty';
import { PaginationMetadata, UsePagination } from '~/react-hooks/usePagination';
import { MediaLibraryAsset } from '~/App/Modules/MyLibrary/Components/InfiniteMediaLibraryItem';
import { usePublishPageContext } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishPageContext';

export interface AssetPageProps {
  perPage?: number;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  isItemSelectDisabled: (id: string) => boolean;
}

export default function AllDisplay({
  perPage = 16,
  scrollContainerRef,
  isItemSelectDisabled,
}: AssetPageProps) {
  const { publish } = usePublishPageContext()!;

  const savedSelectedIds = publish?.publishable_resources || [];

  const publishSelectModalContext = usePublishSelectModalContext()!;

  const {
    setPerPage,
    testimonialsPagination,
    userAssetsPagination,
    webAssetsPagination,
    selectedTestimonialsPagination,
    selectedUserAssetsPagination,
    selectedWebAssetsPagination,
    invalidateAllQueries,
    selectedIds,
    addSelectedId,
    removeSelectedId,
    filteredTags,
    getFilteredTagCollection,
    clearSelectedTags,
  } = publishSelectModalContext;

  useEffect(() => {
    setPerPage(perPage);
  }, [perPage]);

  const paginationData = useMemo(() => {
    const data: UsePagination<MediaLibraryAsset, PaginationMetadata>[] = [];

    if (savedSelectedIds.length && selectedTestimonialsPagination) {
      data.push(selectedTestimonialsPagination);
    }

    if (savedSelectedIds.length && selectedUserAssetsPagination) {
      data.push(selectedUserAssetsPagination);
    }

    if (savedSelectedIds.length && selectedWebAssetsPagination) {
      data.push(selectedWebAssetsPagination);
    }

    if (testimonialsPagination) {
      data.push(testimonialsPagination);
    }

    if (userAssetsPagination) {
      data.push(userAssetsPagination);
    }

    if (webAssetsPagination) {
      data.push(webAssetsPagination);
    }

    return data;
  }, [
    savedSelectedIds,
    selectedTestimonialsPagination,
    selectedUserAssetsPagination,
    selectedWebAssetsPagination,
    testimonialsPagination,
    userAssetsPagination,
    webAssetsPagination,
  ]);

  const { hasData, isLoading } = useMemo(() => {
    return paginationData.reduce(
      (result, datum) => {
        const metadata = datum[2];

        return {
          hasData: result.hasData || (metadata.total ?? 0) > 0,
          isLoading: result.isLoading || metadata.loading,
        };
      },
      {
        hasData: false,
        isLoading: false,
      }
    );
  }, [paginationData]);

  if (Object.keys(filteredTags).length === 0 && !isLoading && !hasData) {
    return <TestimonialsAllEmpty />;
  }

  return (
    <InfiniteMediaLibrary
      perPage={perPage}
      paginationData={paginationData}
      onTagUpdated={invalidateAllQueries}
      orderByCreatedAt={false}
      emptyComponent={
        <EmptyRecordingUploadsAndResponses
          tags={getFilteredTagCollection()}
          clearTags={clearSelectedTags}
        />
      }
      scrollContainerRef={scrollContainerRef}
      gridTileType={GridTileType.Dynamic}
      itemConfig={
        {
          hideInteractionStats: true,
          selectMode: true,
        } as InfiniteMediaItemConfigProps
      }
      addSelectedId={addSelectedId}
      removeSelectedId={removeSelectedId}
      selectedIds={selectedIds}
      isItemSelectDisabled={isItemSelectDisabled}
    />
  );
}
